// internal deps
import { format, isEmpty } from "@swiggy-private/core-ui";
import {
    VALID_PAYMENT_METHODS,
    VALID_PAYMENT_GROUP_TYPES,
    CardBrandDetails,
    CardDetails,
    ProceedNewCardDetails,
} from "@swiggy-private/payment-core";
import { TEXTS } from "@payment-ui/constants/index";

// constants
import {
    CARD_BRAND,
    DEFAULT_CVV_LENGTH,
    AMEX_CARD_CVV_LENGTH,
    CARD_NUMBER_FORMAT_TITLE,
    CARD_ICONS_V4,
    CARD_ENDING_WITH,
    CARD_ISSUER_NAME,
    CARD_TYPE_DEBIT,
    CARD_TYPE_CREDIT,
} from "@payment-ui/constants/Card";
import { CARD_NAME } from "@payment-ui/constants/TurboPayment";

// interfaces
import {
    CardPaymentMethodData,
    CardPaymentGroupOptions,
    CardLogos,
    ICardPaymentGroup,
    CardPaymentMethodMeta,
} from "@payment-ui/interfaces/Card";
import { SimpleCardValidator } from "../types/Juspay";

// test-ids
import {
    CARD_PAYMENT_METHOD_TEST_IDS,
    FOOD_CARD_PAYMENT_METHOD_TEST_IDS,
    NEW_CARD_PAYMENT_METHOD_TEST_IDS,
    NEW_FOOD_CARD_PAYMENT_METHOD_TEST_IDS,
} from "@swiggy-private/payment-test-ids";
import { PaymentMethod, PaymentMethodMeta } from "@payment-ui/interfaces";

// helpers
import { cardValidator as simpleCardValidator } from "./validator";
import { getPaymentMethodPromoMsg } from "./Payment";
import { capitalizeFirstLetter } from "./String";
import { getPaymentParams } from "@payment-ui/components/payment-method/card/helpers";
import { FLOW_TYPE } from "@payment-ui/components/payment-method/card/constants";

export const isAddNewCardPayment = (data: CardPaymentMethodData): boolean => {
    return data.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_CARD;
};

export const isCardPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is CardPaymentMethodData => {
    return [VALID_PAYMENT_GROUP_TYPES.CARD, VALID_PAYMENT_GROUP_TYPES.FOOD_CARD].includes(
        method.groupName.toUpperCase(),
    );
};

export const isAddNewFoodCardPayment = (data: CardPaymentMethodData): boolean => {
    return data.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_FOOD_CARD;
};

export const getCardLogosList = (options: CardPaymentGroupOptions, category?: string): CardLogos => {
    if (category == null) {
        return options.weAcceptAllLogos;
    }
    switch (category.toUpperCase()) {
        case VALID_PAYMENT_GROUP_TYPES.FOOD_CARD:
            return options.weAcceptFoodCardLogos;
        case VALID_PAYMENT_GROUP_TYPES.CARD:
        default:
            return options.weAcceptNonFoodCardLogos;
    }
};

export const isFoodCard = (methodData: CardPaymentMethodData): boolean => {
    return methodData.meta.isFoodCard ?? false;
};

export const isFoodCardGroup = (data: CardPaymentMethodData | ICardPaymentGroup["paymentData"]): boolean => {
    return data.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.FOOD_CARD;
};

export const getCardNumberString = (cardNumber: string | number): string => {
    return cardNumber
        .toString()
        .replace(/\s/g, "")
        .replace(/.{4}(?=.)/g, "$& ")
        .trim();
};

export const getCardMonthString = (month: string): string => {
    const _month = parseInt(month, 10);
    return _month < 10 ? `0${_month}` : _month.toString();
};

export const getTransformValue = ({
    name,
    value,
    details,
}: {
    name: string;
    value: string | number;
    details: { [key: string]: string | number };
}): string | number | undefined => {
    if (name === "cardNumber") {
        if (!/^[0-9\s]*$/.test(String(value))) {
            return undefined;
        }
        return getCardNumberString(value);
    } else if (name === "name") {
        if (!/^[a-zA-Z\s]*$/.test(String(value))) {
            return undefined;
        }
        return String(value).toUpperCase();
    } else if (name === "expiry") {
        if (
            !/^[0-9/]*$/.test(String(value)) ||
            (String(value).indexOf("/") !== -1 && String(value).lastIndexOf("/") !== 2)
        ) {
            return undefined;
        } else if (value.toString().length === 1 && Number(value) > 1) {
            return String(`0${value}/`);
        } else if (value.toString().length === 2) {
            const currentValue = details["expiry"].toString();
            if (currentValue.length === 3) {
                return currentValue.substring(0, 1);
            }
            return String(`${value}/`);
        }
        return String(value);
    } else if (name === "cvv") {
        if (!/^[0-9]*$/.test(String(value))) {
            return undefined;
        }
        return `${value}`;
    } else if (name === "month") {
        return value < 0 || value > 12 ? undefined : value;
    } else if (name === "nickName") {
        if (/^[a-zA-Z\s]*$/.test(String(value))) {
            return value;
        } else {
            return undefined;
        }
    } else {
        return value;
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCardValidator = (number: string): SimpleCardValidator => new (simpleCardValidator as any)(number);

export const getMonthAndYearFromExpiry = (expiry: string): { month: string; year: string } => {
    const [month, year] = expiry.split("/");
    const currentEra = new Date()
        .getFullYear()
        .toString()
        .substr(0, 2);
    return {
        month,
        year: currentEra + year,
    };
};

export const isValidCardNumber = (validator: SimpleCardValidator): boolean => {
    try {
        return validator.validateCard();
    } catch (e) {
        return false;
    }
};

const isValidExpiry = (validator: SimpleCardValidator, expiry: string): boolean => {
    try {
        const { month, year } = getMonthAndYearFromExpiry(expiry);
        return validator.validateExpiry(`${month}/${year}`);
    } catch (e) {
        return false;
    }
};

const isValidCvv = (validator: SimpleCardValidator, cvv: string): boolean => {
    try {
        return validator.validateCvv(cvv);
    } catch (e) {
        return false;
    }
};

const isValidName = (name: string): boolean => {
    try {
        return name.length > 0;
    } catch (e) {
        return false;
    }
};

export const isValidCard = (
    validator: SimpleCardValidator,
    details: {
        cardNumber: string;
        cvv: string;
        name: string;
        expiry: string;
        nickName: string;
    },
): { cardNumber: boolean; cvv: boolean; expiry: boolean; name: boolean; nickName: boolean } => {
    const errors = {
        cardNumber: false,
        cvv: false,
        expiry: false,
        name: false,
        nickName: false,
    };

    errors.cardNumber = !isValidCardNumber(validator);
    errors.expiry = !isValidExpiry(validator, details.expiry);
    errors.cvv = !isValidCvv(validator, details.cvv);

    errors.name = !isValidName(details.name);

    return errors;
};

export function getCardMethodTestIds(
    methodData: CardPaymentMethodData,
): typeof FOOD_CARD_PAYMENT_METHOD_TEST_IDS | typeof CARD_PAYMENT_METHOD_TEST_IDS {
    return isFoodCardGroup(methodData) ? FOOD_CARD_PAYMENT_METHOD_TEST_IDS : CARD_PAYMENT_METHOD_TEST_IDS;
}

export function getNewCardMethodTestIds(
    methodData: CardPaymentMethodData,
): typeof NEW_FOOD_CARD_PAYMENT_METHOD_TEST_IDS | typeof NEW_CARD_PAYMENT_METHOD_TEST_IDS {
    return isFoodCardGroup(methodData) ? NEW_FOOD_CARD_PAYMENT_METHOD_TEST_IDS : NEW_CARD_PAYMENT_METHOD_TEST_IDS;
}

export const isSodexoCard = (data: CardPaymentMethodData["meta"] | CardBrandDetails): boolean =>
    data.cardBrand?.toUpperCase() === CARD_BRAND.SODEXO;

export const getValidCardCvvLength = (methodData: CardPaymentMethodData): number => {
    return methodData.meta.cardBrand?.toUpperCase() === CARD_BRAND.AMEX ? AMEX_CARD_CVV_LENGTH : DEFAULT_CVV_LENGTH;
};

export const isCardMethodSecured = (meta: CardPaymentMethodData["meta"]): boolean => {
    return meta.isSecured || false;
};

export const hasTokeniseSupport = (meta: CardPaymentMethodData["meta"]): boolean => {
    return meta.tokenizeSupport || false;
};

export const getCardBrand = (meta: CardPaymentMethodData["meta"]): string => {
    return meta.cardBrand || "";
};

export const getFormattedCardName = (displayName: string): string => {
    const last4digits = displayName.slice(-4) ?? "";

    return format(CARD_NUMBER_FORMAT_TITLE, last4digits);
};

export const isDebitOrCreditCard = (cardMeta: CardPaymentMethodMeta) => {
    return (
        cardMeta.cardType?.toUpperCase() === CARD_TYPE_DEBIT || cardMeta.cardType?.toUpperCase() === CARD_TYPE_CREDIT
    );
};

export const getCardDisplayName = (
    displayName: string,
    meta: CardPaymentMethodData["meta"],
    isTurboPaymentMethod?: boolean,
): string => {
    if (!isEmpty(meta.nickname)) {
        return meta.nickname;
    }

    if (isTurboPaymentMethod && isDebitOrCreditCard(meta) && !meta.isFoodCard) {
        return format(CARD_NAME, capitalizeFirstLetter(meta.cardType));
    }

    if (!isEmpty(meta.cardIssuer)) {
        return format(CARD_ISSUER_NAME, meta.cardIssuer);
    }

    return getFormattedCardName(displayName);
};

export const getCardPromoMsg = (paymentMethod: CardPaymentMethodData, isTurboPaymentMethod: boolean): string => {
    if (isTurboPaymentMethod) {
        return paymentMethod.meta.promoMsg ?? "";
    }

    return getPaymentMethodPromoMsg(paymentMethod);
};

export const getAriaCardDisplay = (displayName: string, meta: CardPaymentMethodData["meta"]): string => {
    if (!isEmpty(meta.nickname)) {
        return meta.nickname;
    }

    if (!isEmpty(meta.cardIssuer)) {
        return format(CARD_ISSUER_NAME, meta.cardIssuer);
    }

    const last4digits = displayName.slice(-4) ?? "";

    return format(CARD_ENDING_WITH, last4digits.split("").join(" "));
};

export const getCardIcon = (cardBrand: string | null): { id: string; padding: number } | null => {
    if (cardBrand == null) {
        return null;
    }

    return CARD_ICONS_V4[cardBrand.toUpperCase()];
};

export const getCardBrandFormattedString = (text: string, cardBrand: string): string => {
    return text && text.replace(TEXTS.CARD_BRAND, isEmpty(cardBrand) ? TEXTS.CARD_NETWORK : cardBrand);
};

export const isProceedNewCardDetails = (
    details: CardDetails | ProceedNewCardDetails,
): details is ProceedNewCardDetails => {
    return (details as CardDetails).cardFingerprint === undefined;
};

export const getCardPaymentMethodExtraParams = (methodData: CardPaymentMethodData, cvv?: string) => {
    const meta = methodData.meta as CardPaymentMethodMeta;
    const isCardSecured = isCardMethodSecured(meta);
    const isTokeniseSupported = hasTokeniseSupport(meta);
    let paymentMethodExtraParams;

    if (isCardSecured) {
        paymentMethodExtraParams = { cvv, ...getPaymentParams(FLOW_TYPE.SECURED_CARD) };
    } else {
        if (isTokeniseSupported) {
            paymentMethodExtraParams = { cvv, ...getPaymentParams(FLOW_TYPE.SECURED_CARD) };
        } else {
            paymentMethodExtraParams = {
                cvv,
                ...getPaymentParams(FLOW_TYPE.NOT_TOKENIZED_CARD),
            };
        }
    }
    return paymentMethodExtraParams;
};
